import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { StaticContext } from 'react-router';
import { Prompt, RouteComponentProps } from 'react-router-dom';
import Headers from '~/view/containers/Headers/Headers';
import HeaderBack from '~/view/containers/Headers/components/HeaderBack/HeaderBack';
import { SCREENS } from '~/constants/screens';
import ScrollableBlock from '~/view/containers/ScrollableBlock/ScrollableBlock';
import LoadingOverlay from '~/view/components/LoadingOverlay/LoadingOverlay';
import { getPromptProps } from '~/view/components/CustomPrompt/helper';
import { localeStore } from '~/mobx/localesStore';
import './style.scss';
import PromocodeName from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeName/PromocodeName';
import PromocodeValidity from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeValidity/PromocodeValidity';
import PromocodeDiscountType from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeDiscountType/PromocodeDiscountType';
import PromocodeCustomers from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeCustomers/PromocodeCustomers';
import PromocodeApplies from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeApplies/PromocodeApplies';
import PromocodeConditions from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeConditions/PromocodeConditions';
import PromocodeCategory from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeCategory/PromocodeCategory';
import PromocodeBranches from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeBranches/PromocodeBranches';
import PromocodeIdExternal from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeIdExternal/PromocodeIdExternal';
import { appStore } from '~/mobx/appStore';
import { promocodeStore } from '~/view/screens/MarketingScreen/screens/PromocodesScreen/store/promocodeStore';
import { defaultPromocode } from '~/view/screens/MarketingScreen/screens/PromocodesScreen/constants/promocodes';
import MarketingEditSkeletons, {
  MARKETING_EDIT_SKELETONS,
} from '~/view/screens/MarketingScreen/components/MarketingEditSkeletons/MarketingEditSkeletons';
import { scrollToErrorElement } from '~/utils/helpers';
import { confirmDelete, confirmZeroPrice } from '~/utils/modalConfirmations';
import PromocodeActions from '~/view/screens/MarketingScreen/screens/PromocodesScreen/components/PromocodeActions/PromocodeActions';
import PromocodeOfferToggle from '../../components/PromocodeOfferToggle/PromocodeOfferToggle';
import { history } from '~/services/history';
import PromocodeCannotBeEdited from '../../components/PromocodeCannotBeEdited/PromocodeCannotBeEdited';
import PromocodeDescription from '../../components/PromocodeDescription/PromocodeDescription';

type IProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const PromocodeEdit: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const isAdvancedSettingsEnabled: boolean = appStore.isAddonsAvailable.promoCode;

  useEffect(() => {
    if (props.match.params.id) {
      promocodeStore.getPromocode(Number(props.match.params.id));
    } else {
      promocodeStore.validator();
    }

    return promocodeStore.unMount;
  }, []);

  const clickSave = () => {
    // if there are validation errors, then scroll to error and exit without saving
    const isValidCashback = promocodeStore.validateCashback();
    if (!promocodeStore.isValid.all || !isValidCashback) {
      scrollToErrorElement();
      return;
    }

    // Validate the offer only after clicking save
    const isOfferValid = promocodeStore.validateOffer();
    if (!isOfferValid) return;

    // if promocode gives a zero discount, then we ask if user is sure of this and exit without saving
    if (promocodeStore.isZeroDiscount) {
      confirmZeroPrice({ message: localeStore.t('confirm_save_promocode_with_0_discount_amount'), onConfirm: save });
      return;
    }

    save();
  };

  const save = async () => {
    if (Number(props.match.params.id)) {
      const response = await promocodeStore.editPromocode(Number(props.match.params.id));
      if (response) props.history.push(SCREENS.PROMOCODES());
    } else {
      const response = await promocodeStore.createPromocode();
      if (response) props.history.push(SCREENS.PROMOCODES());
    }
  };

  const clickDelete = async () => {
    confirmDelete({
      title: localeStore.t('are_you_sure'),
      message: localeStore.t('txt_cart_item_delete_confirm'),
      onConfirm: async () => {
        const response = await promocodeStore.deletePromocode(Number(props.match.params.id));
        if (response) history.push(SCREENS.PROMOCODES());
      },
    });
  };

  const isShowDelete = () => {
    return !!Number(props.match.params.id);
  };

  if (JSON.stringify(promocodeStore.promocode) === JSON.stringify(defaultPromocode) && promocodeStore.loading) {
    return (
      <div className="d-flex flex-column mt-11">
        <HeaderBack isLoading title={' '} noTranslation path={SCREENS.PROMOCODES()} />
        <MarketingEditSkeletons type={MARKETING_EDIT_SKELETONS.PROMOCODE} />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-grow-1 position-relative">
      <Headers>
        <HeaderBack title={promocodeStore.promocode.promocode ?? ' '} noTranslation path={SCREENS.PROMOCODES()} />
      </Headers>

      <ScrollableBlock showScrollbar className="full-height mt-6 expand-scroll-block">
        {!promocodeStore.isProvideAsDiscount && (!appStore.isAddonsAvailable.wallet || !appStore.isAddonsAvailable.promoCode) ? (
          <PromocodeCannotBeEdited />
        ) : (
          <>
            <PromocodeName disabled={!!props.match.params.id} />
            <PromocodeValidity />
            <PromocodeDiscountType />
            {isAdvancedSettingsEnabled && <PromocodeCustomers />}
            <PromocodeApplies />
            {isAdvancedSettingsEnabled && (
              <>
                <PromocodeConditions />
                <PromocodeCategory />
                <PromocodeBranches />
              </>
            )}
            <PromocodeIdExternal />
            <PromocodeOfferToggle />
            {!!promocodeStore.promocode.is_shown_as_offer && <PromocodeDescription />}
          </>
        )}
        <PromocodeActions onSave={clickSave} onDelete={clickDelete} showDelete={isShowDelete()} />
      </ScrollableBlock>
      <LoadingOverlay visible={promocodeStore.loading} />
      <Prompt when={promocodeStore.isEdit} message={getPromptProps()} />
    </div>
  );
});

export default PromocodeEdit;
